import { mq, spacing, styled, Text } from '@thg-commerce/enterprise-theme'
import { ButtonState, styling } from '@thg-commerce/gravity-elements/Button'
import { Grid, GridItem } from '@thg-commerce/gravity-system'

import { Image } from './elements'

export const WidgetGridItem = styled(GridItem)`
  margin-top: ${spacing(2)};
  margin-bottom: ${spacing(2)};
`

export const RecipeGridItem = styled(GridItem)`
  margin-bottom: ${spacing(3)};
`

export const ElementGridItem = styled(GridItem)`
  margin-bottom: 0;
  word-wrap: break-word;

  p {
    margin: 0 0 ${spacing(2)};
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: ${spacing(5)} 0 ${spacing(2)};
  }

  h1,
  h1 a {
    ${Text('xlarge1', 'alternate')}
  }

  h2,
  h2 a {
    ${Text('large2', 'alternate')}
  }

  h3,
  h3 a {
    ${Text('large1', 'alternate')}
  }

  h4,
  h4 a {
    ${Text('medium1', 'alternate')}
  }

  h5,
  h5 a,
  h6,
  h6 a {
    ${Text('bodyText', 'alternate')}
  }

  img {
    width: 100%;
    height: auto;
  }

  strong {
    ${Text('bodyText', 'alternate')}
    font-size: inherit;
    font-style: inherit;

    ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
      font-size: inherit;
      font-style: inherit;
    }
  }

  em {
    font-style: italic;
    font-size: inherit;
    font-weight: inherit;
  }

  ol {
    counter-reset: item;

    li {
      display: block;

      & > p {
        display: inline;
        margin: ${spacing(1)} 0;
      }

      &:before {
        content: counter(item) '. ';
        counter-increment: item;
        width: 1em;
        display: inline-block;
      }
    }
  }

  ul {
    list-style: disc;
    margin-bottom: ${spacing(2)};
    margin-left: calc(${spacing(2)} + 2px);
  }

  table {
    display: block;
    margin: ${spacing(7)} 0 ${spacing(7)};
    width: 100%;
    overflow-x: auto;

    thead {
      ${Text('bodyText', 'alternate')}
    }

    tr {
      background-color: ${(props) => props.theme.colors.palette.greys.white};
    }

    tr:nth-child(even) {
      background-color: ${(props) => props.theme.colors.palette.greys.lighter};
    }

    td {
      padding: ${spacing(1)};
      border: 1px solid ${(props) => props.theme.colors.palette.greys.light};
    }
  }

  hr {
    margin: ${spacing(2)} 0;
    border: none;
    border-bottom: 1px solid
      ${(props) => props.theme.colors.palette.greys.light};
  }

  h2,
  h3,
  h4,
  h5,
  h6 {
    + div:first-of-type {
      margin-top: ${spacing(3)};

      table {
        margin-top: 0;
      }
    }
  }

  ul + div:first-of-type {
    margin-top: ${spacing(7)};
  }

  blockquote {
    ${Text('large2', 'alternate')};
    display: inline-block;
    text-align: center;
    quotes: '“' '”' '‘' '’';

    &::before {
      content: open-quote;
    }

    &::after {
      content: close-quote;
    }

    &::before,
    &::after {
      ${Text('xlarge2', 'alternate')};
      color: ${(props) => props.theme.colors.palette.accent.base};
    }

    * {
      ${Text('large2', 'alternate')};
      text-align: center;
    }
  }

  sup {
    ${Text('xsmall', 'default')};
    position: relative;
    top: -0.5em;
  }

  sub {
    ${Text('xsmall', 'default')};
    position: relative;
    bottom: -0.25em;
  }

  a {
    ${Text('bodyText', 'alternate')}
    ${(props) =>
      styling({ theme: props.theme, sizing: 'regular', emphasis: 'low' })};
    &:hover {
      ${(props) =>
        styling(
          { theme: props.theme, sizing: 'regular', emphasis: 'low' },
          ButtonState.Hover,
        )};
    }

    &:focus {
      ${(props) =>
        styling(
          { theme: props.theme, sizing: 'regular', emphasis: 'low' },
          ButtonState.Focus,
        )};
    }
    &:active {
      ${(props) =>
        styling(
          { theme: props.theme, sizing: 'regular', emphasis: 'low' },
          ButtonState.Active,
        )};
    }
    &:disabled {
      ${(props) =>
        styling(
          { theme: props.theme, sizing: 'regular', emphasis: 'low' },
          ButtonState.Disabled,
        )};
    }
  }

  p a {
    padding: 0;
  }
`

export const StyledImage = styled(Image)<{
  imageAlignment?: 'left' | 'center' | 'right'
}>`
  max-width: 100%;
  height: auto;
  img {
    margin: 0;
  }

  display: block;
  margin: ${(props) =>
    props.imageAlignment === 'left'
      ? `0 ${spacing(2)} ${spacing(2)} 0`
      : props.imageAlignment === 'right'
      ? `0 ${spacing(2)}`
      : props.imageAlignment === 'center'
      ? `0 auto ${spacing(2)}`
      : '0'};
  float: ${(props) =>
    props.imageAlignment === 'left'
      ? 'left'
      : props.imageAlignment === 'right'
      ? 'right'
      : 'none'};
`

export const StyledGrid = styled(Grid)`
  margin-left: 0;
  margin-right: 0;
  margin-bottom: 0;

  ${ElementGridItem}:first-of-type > *:first-child:not(table) {
    margin-top: 0;
  }

  ${ElementGridItem}:last-of-type > *:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
  }

  ${StyledImage} {
    display: block;
  }
`
